import { useContext, useEffect, useState } from 'react'

import Link from '@atoms/Link'
import Button from '@atoms/Button'
import Logo from '@atoms/Logo'
import Loading from '@atoms/Loading'
import LanguageSwitch from '@molecules/LanguageSwitch'
import Popup from '@molecules/Popup'
import SaveIcon from '@icons/Save'
import UserIcon from '@icons/User'
import HistoryIcon from '@icons/History'
import MenuIcon from '@icons/Menu'
import { StoreContext } from '@models/Store'
import { useScroll } from '@models/Util'
import { getLocalData, setLocalData } from '@models/LocalData'
import { useTranslation } from '@models/Translation'
import dayjsUtil from '@models/DayJS'

import * as style from './style.module.sass'

const Header = () => {
  const { ui, entity, action } = useContext(StoreContext)
  const { banner, showMenu } = ui
  const { setShowMenu } = action
  const { t } = useTranslation()

  const now = dayjsUtil()
  const { isTop, isScrollingDown } = useScroll()
  const closeMenu = () => setTimeout(() => setShowMenu(false), 100)

  const popupKey = 'header_popup'
  const [showPopup, setShowPopup] = useState(false)

  useEffect(() => {
    setTimeout(() => setShowPopup(getLocalData(popupKey) !== 'remove'), 1000)
  }, [])

  const removePopup = () => {
    setShowPopup(false)
    setLocalData(popupKey, 'remove')
  }

  return (
    <div
      className={style.headerContainer}
      data-show-banner={banner !== undefined}
    >
      <header
        className={style.header}
        data-small={!isTop && !showMenu}
        data-background={!showMenu}
        data-scrolling-down={!isTop && !showMenu && isScrollingDown}
      >
        <div className={style.left}>
          <div className={style.languageSwitch}>
            <LanguageSwitch design="white" />
          </div>
          <div className={style.date}>
            {now.format('YYYY.MM.DD')}
            <span>{now.format('ddd').toUpperCase()}</span>
          </div>
          <button
            className={style.menuSwitch}
            data-active={showMenu}
            onClick={() => setShowMenu(!showMenu)}
            data-only="smallTab"
          >
            <MenuIcon />
            <svg width="25" height="25" viewBox="0 0 25 25" fill="none">
              <path
                d="M6.47607 19.1265C6.38818 19.0435 6.32959 18.9458 6.30029 18.8335C6.27588 18.7212 6.27832 18.6089 6.30762 18.4966C6.33691 18.3843 6.39307 18.2866 6.47607 18.2036L11.1636 13.5088L6.47607 8.82129C6.39307 8.73828 6.33691 8.64062 6.30762 8.52832C6.2832 8.41602 6.2832 8.30371 6.30762 8.19141C6.33691 8.0791 6.39307 7.98145 6.47607 7.89844C6.55908 7.81055 6.65674 7.75439 6.76904 7.72998C6.88135 7.70068 6.99365 7.70068 7.10596 7.72998C7.22314 7.75439 7.32324 7.81055 7.40625 7.89844L12.0938 12.5859L16.7812 7.89844C16.8643 7.81055 16.9619 7.75439 17.0742 7.72998C17.1865 7.70068 17.2988 7.70068 17.4111 7.72998C17.5234 7.75439 17.6235 7.81055 17.7114 7.89844C17.7944 7.98145 17.8506 8.0791 17.8799 8.19141C17.9092 8.30371 17.9092 8.41602 17.8799 8.52832C17.8506 8.64062 17.7944 8.73828 17.7114 8.82129L13.0239 13.5088L17.7114 18.2036C17.7944 18.2866 17.8481 18.3843 17.8726 18.4966C17.9019 18.6089 17.9019 18.7212 17.8726 18.8335C17.8481 18.9458 17.7944 19.0435 17.7114 19.1265C17.6284 19.2144 17.5283 19.2705 17.4111 19.2949C17.2988 19.3242 17.1865 19.3242 17.0742 19.2949C16.9619 19.2656 16.8643 19.2095 16.7812 19.1265L12.0938 14.439L7.40625 19.1265C7.32324 19.2095 7.22559 19.2656 7.11328 19.2949C7.00098 19.3242 6.88867 19.3242 6.77637 19.2949C6.66406 19.2656 6.56396 19.2095 6.47607 19.1265Z"
                fill="currentColor"
              />
            </svg>
            <span>{showMenu ? t('mypage.close') : t('mypage.menu')}</span>
          </button>
        </div>

        <div className={style.center}>
          <div className={style.logoContainer}>
            <Link to="/" onClick={closeMenu}>
              <Logo withText />
            </Link>
          </div>
        </div>

        <div className={style.right} data-loading={entity.user === undefined}>
          <Link to="/save" onClick={closeMenu} className={style.save}>
            <SaveIcon />
            {t('mypage.save')}
          </Link>
          <Link to="/history" onClick={closeMenu} className={style.history}>
            <HistoryIcon />
            {t('mypage.history')}
          </Link>
          {entity.user?.isSignIn && (
            <Link to="/save" onClick={closeMenu} className={style.mypage}>
              <UserIcon />
              <span>{t('mypage.mypage')}</span>
            </Link>
          )}
          <button
            className={style.menuSwitch}
            data-active={showMenu}
            onClick={() => setShowMenu(!showMenu)}
          >
            <MenuIcon />
            <svg width="25" height="25" viewBox="0 0 25 25" fill="none">
              <path
                d="M6.47607 19.1265C6.38818 19.0435 6.32959 18.9458 6.30029 18.8335C6.27588 18.7212 6.27832 18.6089 6.30762 18.4966C6.33691 18.3843 6.39307 18.2866 6.47607 18.2036L11.1636 13.5088L6.47607 8.82129C6.39307 8.73828 6.33691 8.64062 6.30762 8.52832C6.2832 8.41602 6.2832 8.30371 6.30762 8.19141C6.33691 8.0791 6.39307 7.98145 6.47607 7.89844C6.55908 7.81055 6.65674 7.75439 6.76904 7.72998C6.88135 7.70068 6.99365 7.70068 7.10596 7.72998C7.22314 7.75439 7.32324 7.81055 7.40625 7.89844L12.0938 12.5859L16.7812 7.89844C16.8643 7.81055 16.9619 7.75439 17.0742 7.72998C17.1865 7.70068 17.2988 7.70068 17.4111 7.72998C17.5234 7.75439 17.6235 7.81055 17.7114 7.89844C17.7944 7.98145 17.8506 8.0791 17.8799 8.19141C17.9092 8.30371 17.9092 8.41602 17.8799 8.52832C17.8506 8.64062 17.7944 8.73828 17.7114 8.82129L13.0239 13.5088L17.7114 18.2036C17.7944 18.2866 17.8481 18.3843 17.8726 18.4966C17.9019 18.6089 17.9019 18.7212 17.8726 18.8335C17.8481 18.9458 17.7944 19.0435 17.7114 19.1265C17.6284 19.2144 17.5283 19.2705 17.4111 19.2949C17.2988 19.3242 17.1865 19.3242 17.0742 19.2949C16.9619 19.2656 16.8643 19.2095 16.7812 19.1265L12.0938 14.439L7.40625 19.1265C7.32324 19.2095 7.22559 19.2656 7.11328 19.2949C7.00098 19.3242 6.88867 19.3242 6.77637 19.2949C6.66406 19.2656 6.56396 19.2095 6.47607 19.1265Z"
                fill="currentColor"
              />
            </svg>
            {showMenu ? t('mypage.close') : t('mypage.menu')}
          </button>
          {!entity.user?.isSignIn && (
            <>
              <div className={style.signupButton}>
                <Button design="border" size="small">
                  <Link to="/signup" onClick={closeMenu}>
                    {t('mypage.registration')} / {t('mypage.login')}
                  </Link>
                </Button>
              </div>
              <Link to="/signup" onClick={closeMenu} className={style.signup}>
                <UserIcon />
              </Link>
            </>
          )}
        </div>

        <div className={style.loading} data-show={entity.user === undefined}>
          <Loading size="small" />
        </div>

        <div
          className={style.popupContainer}
          data-show={
            isTop &&
            entity.user !== undefined &&
            !entity.user.isSignIn &&
            showPopup
          }
        >
          <Popup onClose={removePopup}>
            <div className={style.contents}>
              <div className={style.label}>
                <span>NEW</span> {t('mypage.feature')}
              </div>
              <div
                dangerouslySetInnerHTML={{ __html: t('mypage.featureText') }}
              />
              <Link to="/signup">
                <Button onClick={removePopup}>
                  {t('mypage.registration')} / {t('mypage.login')}
                </Button>
              </Link>
            </div>
          </Popup>
        </div>
      </header>
    </div>
  )
}

export default Header
